import React, { useCallback, useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { googleSheetsService } from '../../google-sheets-service';
import { GOOGLE_CONFIG } from '../../app-config';

type SheetTable = {
  id: string;
  label: string;
  title: string;
  values: string[][];
  error: string | null;
};

function parseSheetsError(error: any, fallback: string): string {
  if (!error) {
    return fallback;
  }

  const code = error?.result?.error?.code ?? error?.status;
  const message = error?.result?.error?.message ?? error?.message ?? fallback;

  if (code === 400 && /Unable to parse range/i.test(message || '')) {
    return 'Unable to read the configured range. Double-check the sheet name and ensure it exists.';
  }

  if (code === 403) {
    return 'Permission denied. Share the spreadsheet with this Google account or adjust its sharing settings.';
  }

  return message || fallback;
}

function columnToLetter(columnIndex: number): string {
  let temp = columnIndex + 1;
  let letter = '';
  while (temp > 0) {
    const mod = (temp - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    temp = Math.floor((temp - mod) / 26);
  }
  return letter;
}

const SAVED_PULSE_DURATION_MS = 900;

const EditableGoogleSheetsTab: React.FC = () => {
  const [tables, setTables] = useState<SheetTable[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('Ready to load sheet data.');
  const [editingCells, setEditingCells] = useState<Set<string>>(new Set<string>());
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set<string>());
  const [editedCells, setEditedCells] = useState<Set<string>>(new Set<string>());
  const [recentlySavedCells, setRecentlySavedCells] = useState<Set<string>>(new Set<string>());
  const cellRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());

  // Helper to extract spreadsheet ID from table ID
  const getSpreadsheetIdFromTableId = (tableId: string): string => {
    const masterSpreadsheetId = (GOOGLE_CONFIG as any).masterSpreadsheet;
    // All tables (including References) use the master spreadsheet
    return masterSpreadsheetId || '';
  };

  const loadSheets = useCallback(async () => {
    setIsLoading(true);
    setStatus('Loading Google Sheets data...');

    const results: SheetTable[] = [];

    const masterSpreadsheetId = (GOOGLE_CONFIG as any).masterSpreadsheet;
    const tableSheets = (GOOGLE_CONFIG as any).tableSheets;

    if (!masterSpreadsheetId || !tableSheets) {
      setStatus('No master spreadsheet configured');
      setIsLoading(false);
      return;
    }

    // Load from master spreadsheet with specific sheet IDs
    for (const [tableName, sheetConfig] of Object.entries(tableSheets)) {
      const config = sheetConfig as { sheetId: number; sheetName: string };

      try {
        const values = await googleSheetsService.getSheetValues(masterSpreadsheetId, config.sheetName);
        results.push({
          id: `${masterSpreadsheetId}-${config.sheetId}`,
          label: tableName,
          title: config.sheetName,
          values,
          error: null
        });
      } catch (error) {
        results.push({
          id: `${masterSpreadsheetId}-${config.sheetId}`,
          label: tableName,
          title: config.sheetName,
          values: [],
          error: parseSheetsError(error, 'Failed to load sheet data.')
        });
      }
    }

    // Load References table if configured
    const referencesTable = (GOOGLE_CONFIG as any).referencesTable;
    if (referencesTable && referencesTable.sheetName) {
      try {
        const values = await googleSheetsService.getSheetValues(masterSpreadsheetId, referencesTable.sheetName);
        results.push({
          id: `${masterSpreadsheetId}-${referencesTable.sheetId}`,
          label: 'References',
          title: referencesTable.sheetName,
          values,
          error: null
        });
      } catch (error) {
        results.push({
          id: `${masterSpreadsheetId}-${referencesTable.sheetId}`,
          label: 'References',
          title: referencesTable.sheetName,
          values: [],
          error: parseSheetsError(error, 'Failed to load references sheet.')
        });
      }
    }

    setTables(results);
    cellRefs.current.clear();
    setEditingCells(new Set<string>());
    setSavingCells(new Set<string>());
    setEditedCells(new Set<string>());
    setRecentlySavedCells(new Set<string>());

    setIsLoading(false);
    setStatus('Sheet data loaded. Click any cell to edit.');
  }, []);

  useEffect(() => {
    loadSheets().catch((error) => {
      console.error('[Editable Sheets] Failed to load sheets', error);
      setStatus('Failed to load sheet data.');
      setIsLoading(false);
    });
  }, [loadSheets]);

  const getCellKey = (tableId: string, rowIndex: number, cellIndex: number) => {
    return `${tableId}-${rowIndex}-${cellIndex}`;
  };

  const handleCellClick = (tableId: string, rowIndex: number, cellIndex: number) => {
    const key = getCellKey(tableId, rowIndex, cellIndex);
    const cell = cellRefs.current.get(key);

    if (cell && !editingCells.has(key) && !savingCells.has(key)) {
      cell.contentEditable = 'true';
      cell.focus();

      // Select all text in the cell
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(cell);
      selection?.removeAllRanges();
      selection?.addRange(range);

      setEditingCells(prev => new Set(prev).add(key));
    }
  };

  const handleCellBlur = async (
    e: React.FocusEvent<HTMLTableCellElement>,
    tableId: string,
    sheetName: string,
    rowIndex: number,
    cellIndex: number,
    originalValue: string
  ) => {
    const key = getCellKey(tableId, rowIndex, cellIndex);
    const newValue = e.currentTarget.textContent || '';

    // Remove from editing state
    setEditingCells(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

    // Make cell non-editable
    e.currentTarget.contentEditable = 'false';

    // If value hasn't changed, do nothing
    if (newValue === originalValue) {
      return;
    }

    // Add to saving state
    setSavingCells(prev => new Set(prev).add(key));

    // Update status
    const columnLetter = columnToLetter(cellIndex);
    const rowNumber = rowIndex + 1; // Convert to 1-based for display
    setStatus(`Saving ${sheetName}!${columnLetter}${rowNumber}...`);

    try {
      // Extract correct spreadsheet ID (might be composite for master spreadsheet)
      const spreadsheetId = getSpreadsheetIdFromTableId(tableId);

      // Create update request for Google Sheets API
      await googleSheetsService.updateCellInSheet(
        spreadsheetId,
        sheetName,
        rowIndex,
        cellIndex,
        newValue
      );

      // Update local state with new value
      setTables(prev => prev.map(table => {
        if (table.id === tableId) {
          const newValues = [...table.values];
          if (!newValues[rowIndex]) {
            newValues[rowIndex] = [];
          }
          newValues[rowIndex][cellIndex] = newValue;
          return { ...table, values: newValues };
        }
        return table;
      }));

      setStatus(`Saved ${sheetName}!${columnLetter}${rowNumber}`);

      setEditedCells(prev => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });

      setRecentlySavedCells(prev => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });

      window.setTimeout(() => {
        setRecentlySavedCells(prev => {
          if (!prev.has(key)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }, SAVED_PULSE_DURATION_MS);
    } catch (error) {
      console.error('Failed to save cell:', error);
      setStatus(`Failed to save: ${parseSheetsError(error, 'Unknown error')}`);

      // Revert the cell content to original value
      e.currentTarget.textContent = originalValue;

      // Add error animation
      const cell = cellRefs.current.get(key);
      if (cell) {
        cell.classList.add('bg-red-100');
        setTimeout(() => {
          cell.classList.remove('bg-red-100');
        }, 1500);
      }
    } finally {
      // Remove from saving state
      setSavingCells(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleCellKeyDown = (e: React.KeyboardEvent<HTMLTableCellElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      // Revert to original value
      const originalValue = e.currentTarget.getAttribute('data-original-value') || '';
      e.currentTarget.textContent = originalValue;
      e.currentTarget.blur();
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-5 py-3 bg-white border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold">Editable Google Sheets</h2>
            <p className="text-sm text-gray-600">Click any cell to edit. Changes are saved to the server automatically. <strong>However, they are not saved to Unity so you will need to refresh the webpage and login again to see the changes.</strong></p>
          </div>
          <button
            type="button"
            className="px-3 py-1.5 bg-unity-blue text-white text-sm rounded hover:bg-unity-blue-dark transition-colors"
            onClick={() => loadSheets()}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className={`mt-2 text-sm ${
          status.includes('Failed') ? 'text-red-600' :
          status.includes('Saved') ? 'text-green-600' :
          'text-gray-500'
        }`}>
          {status}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 bg-gray-50">
        {tables.map((table) => {
          // Determine spreadsheet URL for this table
          let sheetsUrl: string | null = null;

          const masterSpreadsheetId = (GOOGLE_CONFIG as any).masterSpreadsheet;

          if (table.label === 'References') {
            const referencesConfig = (GOOGLE_CONFIG as any).referencesTable;
            if (masterSpreadsheetId && referencesConfig) {
              sheetsUrl = `https://docs.google.com/spreadsheets/d/${masterSpreadsheetId}/edit#gid=${referencesConfig.sheetId || 0}`;
            }
          } else {
            // For other tables, use master spreadsheet with the sheet's gid
            const tableSheets = (GOOGLE_CONFIG as any).tableSheets;
            const sheetConfig = tableSheets?.[table.label];

            if (masterSpreadsheetId && sheetConfig) {
              sheetsUrl = `https://docs.google.com/spreadsheets/d/${masterSpreadsheetId}/edit#gid=${sheetConfig.sheetId}`;
            }
          }

          return (
            <section key={table.id} className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h3 className="font-semibold">{table.label}</h3>
                  {table.title && <span className="text-sm text-gray-500">Tab: {table.title}</span>}
                </div>
                {sheetsUrl && (
                  <a
                    href={sheetsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    Open in Google Sheets
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            {table.error ? (
              <div className="p-4 text-red-600 bg-red-50">{table.error}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {(table.values[0] ?? []).map((cell, idx) => (
                        <th key={idx} className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          {cell}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {table.values.slice(1).map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        {row.map((cell, cellIndex) => {
                          const actualRowIndex = rowIndex + 1; // Skip header row
                          const key = getCellKey(table.id, actualRowIndex, cellIndex);
                          const isEditing = editingCells.has(key);
                          const isSaving = savingCells.has(key);
                          const isEdited = editedCells.has(key);
                          const isRecentlySaved = recentlySavedCells.has(key);

                          return (
                            <td
                              key={cellIndex}
                              ref={(el) => {
                                if (el) cellRefs.current.set(key, el);
                              }}
                              className={clsx(
                                'px-3 py-2 text-sm text-gray-900 cursor-text transition-all duration-200 relative',
                                isEditing && 'ring-2 ring-blue-500 bg-blue-50',
                                isSaving && 'bg-gray-100 text-gray-500 cursor-wait',
                                !isEditing && !isSaving && 'hover:bg-gray-100',
                                !isEditing && !isSaving && isEdited && 'cell-edit-highlight',
                                isRecentlySaved && 'cell-saved-pulse'
                              )}
                              onClick={() => handleCellClick(table.id, actualRowIndex, cellIndex)}
                              onBlur={(e) => handleCellBlur(e, table.id, table.title, actualRowIndex, cellIndex, cell ?? '')}
                              onKeyDown={handleCellKeyDown}
                              data-original-value={cell ?? ''}
                              suppressContentEditableWarning={true}
                            >
                              {isSaving && (
                                <span className="inline-block mr-2">
                                  <svg className="animate-spin h-3 w-3 text-gray-500 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </span>
                              )}
                              {cell ?? ''}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {table.values.length <= 1 && (
                      <tr>
                        <td colSpan={Math.max(table.values[0]?.length ?? 1, 1)} className="px-3 py-4 text-center text-gray-500 italic">
                          This sheet does not contain any rows yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          );
        })}
      </div>
    </div>
  );
};

export default EditableGoogleSheetsTab;
